import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import MemoryRoundedIcon from '@mui/icons-material/MemoryRounded';
import HmiView, { BoxLabel, Outline } from './HmiView';
import { FULL, TARGET, fitAspect, pad } from './screenData';
import { BLUE, EASE, Intro, RED, Reveal } from './ui';
import { StudyText } from './studies';
import styles from './ScreenInspector.module.css';

// The slide follows one check: "Does the circulation pump show Off?" on the Ready screen.
const T = TARGET['overview.circulation_pump'];
const CROP = fitAspect(pad(T.box, 40));
const DECOYS = [TARGET['overview.loading'].anchor, TARGET['overview.motor_winding'].anchor];
const SCAN = 1.3;

function StepViewer({ step, model }) {
    const view = step >= 2 ? CROP : FULL;
    const keep = step >= 2 ? pad(T.box, 2) : T.sample;
    const keepOpacity = step === 1 ? 0.55 : step >= 2 ? 1 : 0;
    const a = T.anchor;
    const [d0, d1] = DECOYS;

    return (
        <div className={styles.frame}>
            <HmiView state="ready" view={view} keep={keep} keepOpacity={keepOpacity} label="Mock HMI screenshot">
                {step === 1 && (
                    <g>
                        <motion.rect
                            initial={{ attrX: d0[0], attrY: d0[1], width: d0[2], height: d0[3], opacity: 0 }}
                            animate={{
                                attrX: [d0[0], d1[0], a[0]],
                                attrY: [d0[1], d1[1], a[1]],
                                width: [d0[2], d1[2], a[2]],
                                height: [d0[3], d1[3], a[3]],
                                opacity: [0.6, 1, 1],
                            }}
                            transition={{ duration: SCAN, times: [0, 0.45, 1], ease: 'easeInOut' }}
                            rx={4}
                            fill="rgba(244,63,94,0.14)"
                            stroke={RED}
                            strokeWidth={3}
                            strokeDasharray="8 6"
                            vectorEffect="non-scaling-stroke"
                        />
                        <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: SCAN, duration: 0.4 }}>
                            <Outline box={pad(T.box, 2)} color={BLUE} fill="rgba(56,189,248,0.10)" animateBox={false} />
                            <BoxLabel box={a} text="anchor 1.000" color={RED} size={14} />
                        </motion.g>
                    </g>
                )}
                {step >= 2 && <Outline box={pad(T.box, 2)} color={BLUE} width={2} animateBox={false} />}
            </HmiView>
            <AnimatePresence>
                {step === 3 && (
                    <motion.span
                        key="model"
                        className={styles.viewerTag}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                    >
                        <MemoryRoundedIcon sx={{ fontSize: 16 }} />
                        {model}
                    </motion.span>
                )}
                {step === 4 && (
                    <motion.span
                        key="value"
                        className={`${styles.viewerTag} ${styles.viewerTagPass}`}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 22 }}
                    >
                        <CheckCircleRoundedIcon sx={{ fontSize: 17 }} />
                        True
                    </motion.span>
                )}
            </AnimatePresence>
        </div>
    );
}

/** One slide, five builds: the down button walks through the steps. */
export default function HowItWorks({ t, step, onStep }) {
    const h = t.how;
    const s = h.steps[step];

    return (
        <>
            <Intro eyebrow={h.eyebrow} title={h.title} sub={h.sub} />
            <div className={styles.how}>
                <Reveal className={styles.howText} delay={0.1}>
                    <ol className={styles.stepList}>
                        {h.steps.map((item, i) => (
                            <li key={item.title}>
                                <button
                                    type="button"
                                    className={`${styles.stepItem} ${i === step ? styles.stepItemOn : ''} ${i < step ? styles.stepItemDone : ''}`}
                                    onClick={() => onStep(i)}
                                    aria-current={i === step ? 'step' : undefined}
                                >
                                    <span className={styles.stepIndex}>{String(i + 1).padStart(2, '0')}</span>
                                    {item.title}
                                </button>
                            </li>
                        ))}
                    </ol>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={step}
                            className={styles.stepDetailBox}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.3, ease: EASE }}
                        >
                            <p className={styles.stepBody}>{s.body}</p>
                            {s.detail && <p className={styles.stepDetail}><StudyText text={s.detail} /></p>}
                            {s.question && (
                                <p className={styles.qa}>
                                    <span>{s.question}</span>
                                    <b>True</b>
                                </p>
                            )}
                            {step === 4 && <p className={styles.stepAssert}><code>assert r.value is True</code></p>}
                        </motion.div>
                    </AnimatePresence>
                </Reveal>
                <Reveal className={styles.howVisual} delay={0.2}>
                    <StepViewer step={step} model={h.model} />
                </Reveal>
            </div>
        </>
    );
}
